









































import { marked } from 'marked';
import DOMPurify  from 'dompurify';
import { BFormTextarea, BDropdown, BDropdownItem, BDropdownDivider } from 'bootstrap-vue';

import { Vue, Component, Prop, Ref } from '$/lib/vueExt';

type FormattingTag = '*' | '**';

/**
 * Renders markdown text as HTML.
 */
@Component({
	model : {
		prop  : 'text',
		event : 'update:text',
	},
	components : {
		BDropdown,
		BDropdownItem,
		BDropdownDivider,
	},
})
export default class Markdown extends Vue {

	@Prop({ required : true })
	readonly text: string;

	/**
	 * A function that formats the text before it is displayed.
	 */
	@Prop({ default : (text: string) => text })
	readonly formatText: (text: string) => string;

	@Prop({ default : 'left' })
	readonly placement: 'left' | 'right' | 'top' | 'bottom';

	@Prop({ default : false })
	readonly showEditor: boolean;

	@Ref()
	textEditor: BFormTextarea;

	boldTag: FormattingTag = '**';
	italicTag: FormattingTag = '*';

	get localText() {
		return this.text;
	}
	set localText(value: string) {
		this.$emit('update:text', value);
	}

	get toolbarButtons() {
		return [
			{ icon : 'type-bold',   action : () => this.addFormattingTag(this.boldTag) },
			{ icon : 'type-italic', action : () => this.addFormattingTag(this.italicTag) },
			{ icon : 'list-ul',     action : () => this.togglePrefixOnSelectedLines('- ') },
			{ icon : 'list-ol',     action : () => this.togglePrefixOnSelectedLines('1. ') },
		];
	}

	get isHorizontal() {
		return [ 'left', 'right' ].includes(this.placement);
	}

	get flexDirection() {
		return {
			left   : 'flex-row',
			right  : 'flex-row-reverse',
			top    : 'flex-column',
			bottom : 'flex-column-reverse',
		}[this.placement] ?? 'flex-row';
	}

	/**
	 * Converts the markdown text to sanitized HTML
	 * @returns The sanitized HTML string
	 */
	get formattedText() {
		return DOMPurify.sanitize(marked.parse(this.formatText(this.localText), { async : false, breaks : true })).replaceAll('<a ', '<a target="_blank" ');
	}

	/**
	 * Handle various keyboard events in the text editor for keyboard shortcuts.
	 * The supported shortcuts are:
	 * - Tab & shift-tab for indentation
	 * - Ctrl+B for bold formatting
	 * - Ctrl+I for italic formatting
	 * @param event The keyboard event
	 */
	handleTextEditorKeydown(event: KeyboardEvent) {
		// Handle tab & shift-tab for indentation
		if (event.key === 'Tab') {
			let selectionStart = this.textEditor.selectionStart;
			let selectionEnd   = this.textEditor.selectionEnd;
			const previousText = this.text;

			// Add/remove a level of indentation
			const selectedLines  = this.togglePrefixOnSelectedLines('\t', !event.shiftKey);
			selectionStart      += event.shiftKey ? -1 : 1;
			selectionEnd        += event.shiftKey ? -selectedLines : selectedLines;

			// Keep the same text selected after the change
			// If the text hasn't changed, leave it as is (shift-tab will otherwise cause the selection to drift if there's no tabs to remove)
			// Yes, setCursorPosition already does a nextTick, but it's needed here too to wait for the localText to update
			// And yes, there does need to be two of them, because localText seems to take 2 ticks to update if this component is in a modal
			this.$nextTick(() => {
				this.$nextTick(() => {
					if (this.localText !== previousText) {
						this.setCursorPosition(selectionStart, selectionEnd);
					}
				});
			});

			event.preventDefault();
		}

		// Shortcut for bold formatting
		if (event.key === 'b' && event.ctrlKey) {
			this.addFormattingTag(this.boldTag);
			event.preventDefault();
		}

		// Shortcut for italic formatting
		if (event.key === 'i' && event.ctrlKey) {
			this.addFormattingTag(this.italicTag);
			event.preventDefault();
		}
	}

	/**
	 * Wraps the selected text in the provided formatting tag.
	 * @param formattingTag The formatting tag to add ('*' for italic, '**' for bold)
	 */
	 addFormattingTag(formattingTag: FormattingTag) {
		const { before, selected, after } = this.getSelectionParts();
		this.localText                    = `${before}${formattingTag}${selected}${formattingTag}${after}`;
		this.setCursorPosition(this.textEditor.selectionStart + formattingTag.length, this.textEditor.selectionEnd + formattingTag.length);
	}

	/**
	 * Break the selection up into the following parts:
	 * - before: The text before the selection
	 * - selected: The selected text, excluding any spaces at the beginning or end
	 * - after: The text after the selection
	 *
	 * @returns The selection parts
	 */
	 getSelectionParts() {
		// Get the selection from the text editor
		let [ before, selected, after ] = _.splitByIndex(this.text, this.textEditor.selectionStart, this.textEditor.selectionEnd);

		if (_.isNotEmpty(selected)) {
			// If the selected text has some leading or trailing spaces, outside of the formatting tag when composing the new text
			// The markdown formatting breaks for things like "**example **text", so we try to make that turn into "**example** text" instead
			// This is useful because in the text editor, double clicking a word will select the word and the spaces after it
			const leadingSpaces  = selected.match(/^\s*/)?.[0] || '';
			const trailingSpaces = selected.match(/\s*$/)?.[0] || '';
			selected             = selected.slice(leadingSpaces.length, selected.length - trailingSpaces.length);
			before               = before + leadingSpaces;
			after                = trailingSpaces + after;
		}

		return { before, selected, after };
	}

	/**
	 * Adds/removes a prefix to all lines that are part of the current selection.
	 * @param prefix The prefix string to add to each selected line
	 * @param toggle Whether to add (true) or remove (false) the prefix. Defaults to true.
	 * @returns The number of lines in the selection
	 */
	togglePrefixOnSelectedLines(prefix: string, toggle = true) {
		const lines = this.text.split('\n');

		// Find which lines are affected by the selection
		const startLine = this.positionToLineIndex(this.textEditor.selectionStart, lines);
		const endLine   = this.positionToLineIndex(this.textEditor.selectionEnd, lines);

		// Process each affected line
		for (let i = startLine; i <= endLine; i++) {
			lines[i] = toggle ? `${prefix}${lines[i]}` : lines[i].replace(prefix, '');
		}

		this.localText = lines.join('\n');
		return endLine - startLine + 1;
	}

	/**
	 * Convert a character position in the text to a line index.
	 * Used to determine which lines are affected by a text selection.
	 * @param position The character position in the text
	 * @param lines Array of text lines to search through
	 * @returns The zero-based line index where the position falls
	 */
	positionToLineIndex(position: number, lines: string[]) {
		let currentPos = 0;
		for (let i = 0; i < lines.length; i++) {
			const lineLength = lines[i].length;

			if (currentPos <= position && position <= currentPos + lineLength) {
				return i;
			}

			currentPos += lineLength + 1;  // +1 for newline character
		}
	}

	/**
	 * Set the cursor position in the text editor and focus it.
	 * Uses $nextTick to ensure the DOM is updated before setting focus.
	 * There's two of them because there appears to be a two tick delay for DOM updates if this component is in a modal.
	 * If both start and end are provided, the cursor will be set to the range between them.
	 * Otherwise, the cursor will be set to the start position.
	 * @param start The character position to set the cursor to
	 * @param end The character position to set the cursor to (optional)
	 */
	 setCursorPosition(start: number, end?: number) {
		end ??= start;
		this.$nextTick(() => {
			this.$nextTick(() => {
				this.textEditor.setSelectionRange(start, end);
				this.textEditor.focus();
			});
		});
	}

}
